import {FormikProps} from 'formik';
import {every, forEach, get, map} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Accordion, AccordionDetails, AccordionTitle, Button, ConfirmModal} from 'components';
import {WeekDay} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {WeekDayWorkingHours, WorkingHours as WorkingHoursType, WorkingHoursInterval} from 'types';
import {workingHoursUtils} from 'utils';

import {MultiInputInterval} from '../MultiInputInterval';
import {WorkingHoursItemTitle} from '../WorkingHoursItemTitle';
import {FIELDS} from '../constants';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.WORKING_HOURS;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type WeekDayAccordionProps = {
    day: WeekDay;
    workingHoursItem: WeekDayWorkingHours;
    values: WorkingHoursType;
    getIntervalPath: (day: WeekDay) => string;
    applyToAll: (intervals: WorkingHoursInterval[]) => void;
};

const WeekDayAccordion: React.FC<WeekDayAccordionProps> = ({values, day, workingHoursItem, getIntervalPath, applyToAll}) => {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const intervalsPath = useMemo(() => getIntervalPath(day), [day, getIntervalPath]);
    const intervals = useMemo(() => get(values, intervalsPath), [values, intervalsPath]);

    const hasValidIntervals = useMemo(() =>
        every(intervals, (interval) => workingHoursUtils.validateInterval(interval, t).isValid), [intervals, t]);

    return (
        <Accordion
            key={day}
            shadow={false}
            expanded={expanded}
            className='tw-mb-4 tw-py-4 tw-border tw-border-default tw-rounded-lg'
            onChange={setExpanded}
        >
            <AccordionTitle iconPlacement='right' styled={false}>
                <WorkingHoursItemTitle item={workingHoursItem} day={day} showExtraInfo={!expanded} />
            </AccordionTitle>
            <AccordionDetails className='tw-mt-4'>
                <MultiInputInterval name={intervalsPath} itemsPath={intervalsPath} values={values} />
                <div className='tw-mt-4'>
                    <Button
                        disabled={!hasValidIntervals}
                        icon='duplicate'
                        onClick={() => applyToAll(workingHoursItem.intervals)}
                    >
                        {t(T_KEYS_COMMON.BUTTONS.APPLY_TO_ALL)}
                    </Button>
                </div>
            </AccordionDetails>
        </Accordion>
    );
};

type Props = FormikProps<WorkingHoursType>;

export const WeekHours: React.FC<Props> = ({values, setFieldValue}) => {
    const {t} = useTranslation();
    const [confirmApplyToAllModalProps, setConfirmApplyToAllModalProps] = useState<{
        open: boolean,
        intervals?: WorkingHoursInterval[]
    }>({open: false});

    const getDayIntervalsPath = useCallback((day: WeekDay) => `${FIELDS.WEEK_HOURS}[${day}].${FIELDS.INTERVALS}`, []);

    const applyToAll = useCallback((intervals: WorkingHoursInterval[]) => {
        forEach(values.weekHours, (_, day) => {
            setFieldValue(getDayIntervalsPath(day as WeekDay), intervals);
        });
    }, [values, setFieldValue, getDayIntervalsPath]);

    const handleOpenApplyToAllModal = useCallback((intervals: WorkingHoursInterval[]) => {
        setConfirmApplyToAllModalProps({open: true, intervals});
    }, []);

    const handleConfirmApplyToAll = useCallback((confirm: boolean) => {
        if (confirm) {
            applyToAll(confirmApplyToAllModalProps.intervals);
        }
        setConfirmApplyToAllModalProps({open: false});
    }, [confirmApplyToAllModalProps, applyToAll]);

    return (
        <div className='2xl:tw-w-1/2'>
            <div className='tw-ts-h5-bold'>{t(T_KEYS.WEEK_HOURS.TITLE)}</div>
            <div className='tw-mt-4'>
                {map(values.weekHours, (item: WeekDayWorkingHours, day: WeekDay) => (
                    <WeekDayAccordion
                        key={day}
                        day={day}
                        workingHoursItem={item}
                        values={values}
                        getIntervalPath={getDayIntervalsPath}
                        applyToAll={handleOpenApplyToAllModal}
                    />
                ))}
            </div>
            <ConfirmModal
                fullWidth
                maxWidth='sm'
                {...confirmApplyToAllModalProps}
                content={t(T_KEYS.WEEK_HOURS.APPLY_TO_ALL_MODAL.LABEL)}
                onClose={handleConfirmApplyToAll}
            />
        </div>
    );
};